import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useBookingT } from "@/hooks/use-translations"

type Props = {
  categories: string[]
  active: string
  onChange: (category: string) => void
}

const ALL = "all"

export function CategoryTabs({ categories, active, onChange }: Props) {
  const t = useBookingT()
  const [sheetOpen, setSheetOpen] = React.useState(false)

  const items: Array<{ id: string; label: string }> = React.useMemo(
    () => [
      { id: ALL, label: t("integrations_category_all", "All") },
      ...categories.map((c) => ({ id: c, label: c })),
    ],
    [categories, t],
  )

  const activeLabel = items.find((i) => i.id === active)?.label ?? items[0].label

  const handleSelect = (id: string) => {
    onChange(id)
    setSheetOpen(false)
  }

  const renderChips = (closeOnSelect: boolean) => (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const isActive = active === item.id
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => (closeOnSelect ? handleSelect(item.id) : onChange(item.id))}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
              isActive
                ? "border-brand-500 bg-brand-500 text-white"
                : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-500",
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )

  return (
    <>
      {/* Desktop: inline chip list */}
      <div className="hidden md:block">{renderChips(false)}</div>

      {/* Mobile: trigger button → bottom Sheet with chips */}
      <div className="md:hidden">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="truncate">
                {t("integrations_category_label", "Category")}: {activeLabel}
              </span>
              <ChevronDown className="size-4 opacity-50 shrink-0" />
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-lg">
            <SheetHeader>
              <SheetTitle>
                {t("integrations_filter_by_category", "Filter by category")}
              </SheetTitle>
            </SheetHeader>
            <div className="pt-4">{renderChips(true)}</div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}
